import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Spinner } from 'react-bootstrap';
import { Doughnut, Bar } from 'react-chartjs-2';
import { chatAPI } from '../api';

const DashboardPage = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await chatAPI.getTraders();
      console.log('API Response:', response.data);
      
      const traders = Array.isArray(response.data) ? response.data : [];
      
      if (traders.length === 0) {
        console.error('No traders data');
        setStats(null);
        setLoading(false);
        return;
      }
      
      const totalTraders = traders.length;
      const avgWinRate = (traders.reduce((sum, t) => 
        sum + (t.performance?.win_rate || 0), 0) / totalTraders).toFixed(1);
      const avgSharpe = (traders.reduce((sum, t) => 
        sum + (t.performance?.sharpe_ratio || 0), 0) / totalTraders).toFixed(2);
      
      const sortedTraders = [...traders].sort((a, b) => 
        (b.performance?.sharpe_ratio || 0) - (a.performance?.sharpe_ratio || 0)
      );
      
      // 스타일별 분포
      const styleCounts = {};
      traders.forEach(t => {
        const style = t.profile?.trading_style || '기타';
        styleCounts[style] = (styleCounts[style] || 0) + 1;
      });
      
      // 경력별 평균 승률
      const expGroups = {
        '1년 미만': [],
        '1-3년': [],
        '3-5년': [],
        '5년 이상': []
      };
      
      traders.forEach(t => {
        const exp = t.profile?.years_experience || 0;
        const winRate = t.performance?.win_rate || 0;
        if (exp < 1) expGroups['1년 미만'].push(winRate);
        else if (exp < 3) expGroups['1-3년'].push(winRate);
        else if (exp < 5) expGroups['3-5년'].push(winRate);
        else expGroups['5년 이상'].push(winRate);
      });
      
      const expAvg = {};
      Object.keys(expGroups).forEach(key => {
        const arr = expGroups[key];
        expAvg[key] = arr.length > 0 
          ? (arr.reduce((a,b) => a+b, 0) / arr.length).toFixed(1)
          : 0;
      });
      
      setStats({
        totalTraders,
        avgWinRate,
        avgSharpe,
        topTraders: sortedTraders.slice(0, 5),
        styleCounts,
        expAvg
      });
    } catch (error) {
      console.error('Stats error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return (
    <div className="text-center mt-5">
      <Spinner animation="border" variant="primary" />
    </div>
  );

  if (!stats) return (
    <div className="text-center mt-5">
      <p className="text-danger">데이터를 불러오는데 실패했습니다.</p>
    </div>
  );

  return (
    <div className="dashboard-page">
      <h2 className="mb-4">대시보드</h2>
      
      <Row className="g-3 mb-4">
        <Col md={4}>
          <Card className="text-center">
            <Card.Body>
              <h3 className="text-primary">{stats.totalTraders}</h3>
              <p className="text-muted mb-0">총 트레이더</p>
            </Card.Body>
          </Card>
        </Col>
        
        <Col md={4}>
          <Card className="text-center">
            <Card.Body>
              <h3 className="text-success">{stats.avgWinRate}%</h3>
              <p className="text-muted mb-0">평균 승률</p>
            </Card.Body>
          </Card>
        </Col>
        
        <Col md={4}>
          <Card className="text-center">
            <Card.Body>
              <h3 className="text-info">{stats.avgSharpe}</h3>
              <p className="text-muted mb-0">평균 샤프 비율</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card>
        <Card.Header>
          <h5 className="mb-0">상위 성과자</h5>
        </Card.Header>
        <Card.Body>
          <div className="table-responsive">
            <table className="table table-hover">
              <thead>
                <tr>
                  <th>순위</th>
                  <th>이름</th>
                  <th>승률</th>
                  <th>샤프 비율</th>
                  <th>총 손익</th>
                </tr>
              </thead>
              <tbody>
                {stats.topTraders.map((trader, idx) => (
                  <tr key={idx}>
                    <td>{idx + 1}</td>
                    <td>{trader.profile?.name || 'N/A'}</td>
                    <td>{(trader.performance?.win_rate || 0).toFixed(1)}%</td>
                    <td>{(trader.performance?.sharpe_ratio || 0).toFixed(2)}</td>
                    <td className={(trader.performance?.total_pnl || 0) > 0 ? 'text-success' : 'text-danger'}>
                      ${(trader.performance?.total_pnl || 0).toFixed(0)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card.Body>
      </Card>
      
      <Row className="g-3 mt-4">
        <Col md={6}>
          <Card>
            <Card.Header>
              <h5 className="mb-0">거래 스타일 분포</h5>
            </Card.Header>
            <Card.Body>
              <Doughnut
                data={{
                  labels: Object.keys(stats.styleCounts),
                  datasets: [{
                    data: Object.values(stats.styleCounts),
                    backgroundColor: [
                      'rgba(0, 71, 171, 0.8)',
                      'rgba(30, 136, 229, 0.8)',
                      'rgba(0, 51, 128, 0.8)',
                      'rgba(100, 150, 230, 0.8)'
                    ]
                  }]
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: true,
                  plugins: {
                    legend: {
                      position: 'bottom'
                    }
                  }
                }}
              />
            </Card.Body>
          </Card>
        </Col>
        
        <Col md={6}>
          <Card>
            <Card.Header>
              <h5 className="mb-0">경력별 평균 승률</h5>
            </Card.Header>
            <Card.Body>
              <Bar
                data={{
                  labels: Object.keys(stats.expAvg),
                  datasets: [{
                    label: '평균 승률 (%)',
                    data: Object.values(stats.expAvg),
                    backgroundColor: 'rgba(0, 71, 171, 0.8)'
                  }]
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: true,
                  plugins: {
                    legend: {
                      display: false
                    }
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                      max: 100
                    }
                  }
                }}
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default DashboardPage;
